import { faAt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Dialog from '@mui/material/Dialog';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import LoginServices from '../Login/LoginServices';
import KeychainLogin from './KeychainLogin';
import AuthMethodButton from '../Onboarding/AuthMethodButton';
import JoinProofChip from '../Onboarding/JoinProofChip';
import JournalDivider from '../Onboarding/JournalDivider';
import { figtree } from '../Landing/fonts';
import {
  Accent,
  DisplayHeading,
  GhostButton,
  MUTED,
  PlaneIcon,
} from '../Landing/ui';

const EmailOtpSignup = dynamic(() => import('../Onboarding/EmailOtpSignup'), {
  ssr: false,
});

// Boarding-pass styled signup dialog. The MUI Dialog stays as the invisible
// behavioral shell (focus trap, fullScreen below md, backdrop guard) — all
// visible styling is expedition-journal Tailwind.
const JoinDialog = props => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [hiveKeychain, setHiveKeychain] = useState(false);
  const [showEmail, setShowEmail] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.hive_keychain) {
      setHiveKeychain(true);
    }
  }, []);

  const { open, handleClose } = props;

  const switchLink = (
    <div className="tw-pb-2 tw-text-center">
      <button
        type="button"
        onClick={props.switch}
        className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-[15px] tw-font-semibold tw-text-primary-700 dark:tw-text-primary-300 hover:tw-underline"
      >
        Sign in instead
      </button>
    </div>
  );

  return (
    <>
      <Dialog
        disableEscapeKeyDown
        maxWidth={hiveKeychain ? 'xs' : false}
        PaperProps={{
          className: `tw-bg-[#FFFDF6] dark:tw-bg-[#15231E] ${
            fullScreen ? '' : 'tw-rounded-3xl'
          }`,
          style: hiveKeychain ? {} : { maxWidth: '950px', width: '100%' },
        }}
        fullScreen={fullScreen}
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
      >
        <div
          className={`${figtree.className} signup-flow tw-relative ${
            fullScreen ? 'tw-flex-1 tw-overflow-y-auto tw-pb-48' : ''
          }`}
        >
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="tw-absolute tw-right-3 tw-top-3 tw-z-10 tw-flex tw-h-10 tw-w-10 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-text-[#5F6F69] dark:tw-text-[#93A69E] hover:tw-bg-[#1D2D28]/5 dark:hover:tw-bg-white/10"
          >
            <svg
              className="tw-h-5 tw-w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>

          {hiveKeychain ? (
            <div className="tw-px-6 tw-pb-6 tw-pt-10 sm:tw-px-8">
              <div className="tw-mb-6 tw-text-center">
                <p className="tw-mb-3 tw-mt-0 tw-font-mono tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.28em] tw-text-primary-700 dark:tw-text-primary-300">
                  Boarding pass · new traveler
                </p>
                <DisplayHeading
                  as="h2"
                  className="tw-text-2xl tw-leading-tight"
                  id="form-dialog-title"
                >
                  Join with Hive <Accent>Keychain</Accent>.
                </DisplayHeading>
                <div className="tw-mt-3">
                  <JoinProofChip />
                </div>
                <p className={`${MUTED} tw-mb-0 tw-mt-3 tw-text-sm`}>
                  To join TravelFeed, please enter your Hive username and click
                  the Keychain button
                </p>
              </div>
              <KeychainLogin journal setHiveKeychain={setHiveKeychain} />
              <JournalDivider label="Or sign up with" className="tw-my-5" />
              <div className="tw-text-center">
                <GhostButton onClick={() => setHiveKeychain(false)}>
                  Use a different method to sign up
                </GhostButton>
              </div>
            </div>
          ) : (
            <div className="tw-px-6 tw-pb-6 tw-pt-10 sm:tw-px-8">
              {/* Header */}
              <div className="tw-mb-7 tw-text-center">
                <p className="tw-mb-3 tw-mt-0 tw-font-mono tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.28em] tw-text-primary-700 dark:tw-text-primary-300">
                  Boarding pass · new traveler
                </p>
                <DisplayHeading
                  as="h2"
                  className="tw-text-2xl tw-leading-tight sm:tw-text-3xl"
                  id="form-dialog-title"
                >
                  {props.text ? (
                    <>Join TravelFeed{props.text}</>
                  ) : (
                    <>
                      Start your travel <Accent>blog</Accent>.
                    </>
                  )}
                </DisplayHeading>
                <div className="tw-mt-3">
                  <JoinProofChip />
                </div>
              </div>

              <div className="tw-grid tw-grid-cols-1 tw-gap-6 md:tw-grid-cols-[1.15fr_auto_1fr] md:tw-gap-8">
                {/* Email signup (desktop always; mobile after choosing Email) */}
                {(showEmail || !fullScreen) && (
                  <div>
                    <p
                      className={`${MUTED} tw-mb-3 tw-mt-0 tw-text-sm tw-font-medium`}
                    >
                      Sign up with email
                    </p>
                    <EmailOtpSignup journal />
                  </div>
                )}

                {/* Tear-off perforation */}
                <JournalDivider
                  vertical
                  className="tw-hidden md:tw-block"
                />

                {/* Service stub */}
                <div>
                  {fullScreen && !showEmail ? (
                    <div className="tw-mb-2">
                      <AuthMethodButton
                        primary
                        onClick={() => setShowEmail(true)}
                        logo={
                          <FontAwesomeIcon
                            style={{ width: 22, height: 22 }}
                            icon={faAt}
                            className="tw-text-white"
                          />
                        }
                        name="Email"
                      />
                    </div>
                  ) : (
                    <JournalDivider
                      label="Or via"
                      className="tw-mb-3 md:tw-mb-3 md:tw-mt-1"
                    />
                  )}
                  <LoginServices journal />

                  {/* Boarding-stub flavor artifact */}
                  <div className="tw-mt-6 tw-hidden tw-items-center tw-justify-between tw-font-mono tw-text-[9px] tw-uppercase tw-tracking-[0.18em] tw-text-[#8A958F] md:tw-flex">
                    <span>
                      From
                      <br />
                      <span className="tw-text-[#5F6F69] dark:tw-text-[#93A69E]">
                        Someday
                      </span>
                    </span>
                    <PlaneIcon className="tw-h-3.5 tw-w-3.5 tw-rotate-90 tw-text-primary-600/60 dark:tw-text-primary-300/50" />
                    <span className="tw-text-right">
                      To
                      <br />
                      <span className="tw-text-[#5F6F69] dark:tw-text-[#93A69E]">
                        Published
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <JournalDivider label="Already aboard?" className="tw-my-5" />
              {switchLink}
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
};

JoinDialog.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
};

export default JoinDialog;
